import { ArrowRight, Box, Heart, MessageCircle, Trophy } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { useScrollPosition } from '../contexts/ScrollPositionContext';
import { getWillById } from '../services/willService';
import { useState, useRef, useEffect } from 'react';
import { File, ChevronLeft, ChevronRight, Play, Paperclip } from 'lucide-react';
import '../styles/media.css';
import AttachmentIcon from '@mui/icons-material/AttachFile';
import { Tooltip } from '@mui/material';
import { toast } from "sonner";
import { getAccessToken } from "../services/authService";
import { authenticatedPost } from "../utils/apiUtils";
import FileAttachmentIndicator from './FileAttachmentIndicator';
import ShareMenu from './ShareMenu';
import CommentModal from './CommentModal';
import OfferFormModal from './OfferFormModal';

interface Media {
  id: string;
  url: string;
  name: string;
  type: string;
  mimeType: string;
  size: number;
}

interface WillCardProps {
  id: string;
  title: string;
  description: string;
  category: string;
  urgency: "low" | "medium" | "high";
  reward: string | null;
  fulfillmentTime: string;
  wanterName: string;
  createdAt: string;
  location: string;
  status: string;
  wanterId: string;
  className?: string;
  lastElementRef?: (node: HTMLElement | null) => void;
  viewMode?: 'grid' | 'list';
  media?: Media[];
  onLikeToggle?: (willId: string, isLiked: boolean) => void;
}

const WillCard = ({
  className = '',
  lastElementRef,
  viewMode = 'grid',
  media = [],
  onLikeToggle,
  ...props
}: WillCardProps) => {
  const { language } = useLanguage();
  const { saveScrollPosition } = useScrollPosition();
  const [isPrefetching, setIsPrefetching] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchEndX, setTouchEndX] = useState<number | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState<'left' | 'right' | null>(null);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [liking, setLiking] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const navigate = useNavigate();
  
  const urgencyColors = {
    low: "bg-green-100 text-green-800",
    medium: "bg-yellow-100 text-yellow-800",
    high: "bg-red-100 text-red-800"
  };

  const containsHebrew = (text: string) => {
    return /[\u0590-\u05FF]/.test(text);
  };

  const isRTL = containsHebrew(props.title) || containsHebrew(props.description);
  
  const handleWillClick = () => {
    const currentPosition = window.scrollY;
    saveScrollPosition('feedScroll', currentPosition);
  };
  
  const handleMouseEnter = () => {
    if (!isPrefetching) {
      setIsPrefetching(true);
      getWillById(props.id).catch(() => {
      });
    }
  };
  
  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (media && media.length > 0) {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % media.length);
    }
  };

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (media && media.length > 0) {
      setCurrentImageIndex((prevIndex) => (prevIndex - 1 + media.length) % media.length);
    }
  };
  
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  };
  
  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEndX(e.touches[0].clientX);
  };
  
  const handleTouchEnd = () => {
    if (!touchStartX || !touchEndX) return;
    
    const difference = touchStartX - touchEndX;
    const minSwipeDistance = 50;
    
    if (difference > minSwipeDistance && media.length > 1) {
      setTransitionDirection('left');
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % media.length);
        setIsTransitioning(false);
        setTransitionDirection(null);
      }, 300);
    } else if (difference < -minSwipeDistance && media.length > 1) {
      setTransitionDirection('right');
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex - 1 + media.length) % media.length);
        setIsTransitioning(false);
        setTransitionDirection(null);
      }, 300);
    }
    
    setTouchStartX(null);
    setTouchEndX(null);
  };
  
  const getMediaContent = (mediaItem: Media, index: number) => {
    if (mediaItem.type === 'image') {
      return (
        <img 
          src={mediaItem.url} 
          alt={`Attachment ${index + 1}`}
          className="will-card-image"
        />
      );
    } else if (mediaItem.type === 'video') {
      return (
        <div className="video-thumbnail">
          <img 
            src={mediaItem.url.replace(/\.[^/.]+$/, ".jpg")} 
            alt={`Video ${index + 1}`}
            className="will-card-image"
            onError={(e) => {
              e.currentTarget.src = "https://via.placeholder.com/300x200?text=Video";
            }}
          />
          <div className="video-play-button">
            <Play size={24} />
          </div>
        </div>
      );
    } else {
      return (
        <div className="document-thumbnail">
          <div className="file-icon">
            <File size={32} />
          </div>
          <div className="file-name">{mediaItem.name}</div>
        </div>
      );
    }
  };
  
  const visualMedia = media.filter(item => 
    item.type === 'image' || 
    item.type === 'video' || 
    item.mimeType?.startsWith('image/') || 
    item.mimeType?.startsWith('video/')
  );

  const documentMedia = media.filter(item => 
    (item.type !== 'image' && item.type !== 'video') && 
    (!item.mimeType?.startsWith('image/') && !item.mimeType?.startsWith('video/'))
  );
  
  const handleLikeToggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!getAccessToken()) {
      toast.error("Please sign in to like this want");
      navigate('/signin');
      return;
    }
    
    try {
      setLiking(true);
      
      const response = await authenticatedPost(
        `${import.meta.env.VITE_API_URL}/wills/${props.id}/like`,
        {}
      );
      
      if (response.ok) {
        // Toggle the like state locally
        const newLikedState = !isLiked;
        setIsLiked(newLikedState);
        setLikeCount(prev => newLikedState ? prev + 1 : Math.max(0, prev - 1));
        
        if (onLikeToggle) {
          onLikeToggle(props.id, newLikedState);
        }
      } else {
        throw new Error(`Failed to ${isLiked ? 'like' : 'like'} want`);
      }
    } catch (error) {
      console.error('Failed to toggle like:', error);
      toast.error(`Failed to ${isLiked ? 'like' : 'like'} want`);
    } finally {
      setLiking(false);
    }
  };
  
  const handleCommentClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!getAccessToken()) {
      toast.error("Please sign in to comment");
      navigate('/signin');
      return;
    }
    
    setShowCommentModal(prevState => !prevState);
  };
  
  const handleOfferClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!getAccessToken()) {
      toast.error("Please sign in to make an offer");
      navigate('/signin');
      return;
    }
    
    setShowOfferModal(prevState => !prevState);
  };
  
  return (
    <div 
      ref={lastElementRef} 
      className={`block will-card rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-all ${className} ${
        viewMode === 'list' ? 'flex flex-row border border-gray-100 rounded-xl' : 'card'
      }`}
      dir={isRTL ? "rtl" : "ltr"}
      onMouseEnter={handleMouseEnter}
    >
      {viewMode === 'list' ? (
        <>
          <div className="flex-shrink-0 w-[120px] h-[120px] relative overflow-hidden bg-gray-100">
            {visualMedia.length > 0 ? (
              <div 
                className="will-card-media instagram-style h-full"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <div className={`media-slider h-full ${isTransitioning ? `sliding-${transitionDirection}` : ''}`}>
                  {getMediaContent(visualMedia[currentImageIndex], currentImageIndex)}
                </div>
                
                {visualMedia.length > 1 && (
                  <>
                    <div className="image-dots absolute bottom-1 left-0 right-0 flex justify-center">
                      {visualMedia.map((_, index) => (
                        <span 
                          key={index} 
                          className={`dot ${index === currentImageIndex ? 'active' : ''}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-50">
                <Box className="h-8 w-8 text-gray-400" />
              </div>
            )}
          </div>
          
          <div className="flex-grow p-4">
            <Link to={`/will/${props.id}`} state={{ will: { ...props, media } }} onClick={handleWillClick}>
              <div className="flex items-center space-x-2 mb-2">
                <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-800">
                  {props.category}
                </span>
                <span className={`px-2 py-0.5 text-xs rounded-full ${urgencyColors[props.urgency]}`}>
                  {props.urgency}
                </span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-1">{props.title}</h3>
            </Link>
            
            {props.location && (
              <div className="text-xs text-gray-500 mt-1 mb-2">
                <span className="inline-flex items-center">
                  📍 {props.location}
                </span>
              </div>
            )}
            
            <div className="flex items-center justify-between mt-auto text-sm">
              <div className="flex items-center gap-3">
                <button 
                  onClick={handleLikeToggle}
                  className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <Heart className={`h-4 w-4 ${isLiked ? 'fill-red-500 text-red-500' : ''}`} />
                  <span>{likeCount > 0 ? likeCount : ''}</span>
                </button>
                
                <button
                  onClick={handleCommentClick}
                  className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <MessageCircle className="h-4 w-4" />
                </button>
                
                <button
                  onClick={handleOfferClick}
                  className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <Trophy className="h-4 w-4" />
                </button>
                
                {documentMedia.length > 0 && (
                  <FileAttachmentIndicator count={documentMedia.length} className="text-gray-600" />
                )}
              </div>
              
              {props.reward && (
                <span className="font-bold text-black">{props.reward}</span>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col h-full">
          <div className="px-3 py-2 flex items-center">
            <Link to={`/profile/${props.wanterId}`} className="flex items-center space-x-2">
              <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
                {props.wanterName ? props.wanterName.charAt(0).toUpperCase() : '?'}
              </div>
              <span className="text-sm font-medium text-gray-900">{props.wanterName || 'Anonymous'}</span>
            </Link>
            <div className="ml-auto">
              <ShareMenu 
                willId={props.id} 
                title={props.title} 
                position="bottom"
                className="text-gray-600"
              />
            </div>
          </div>
          
          {visualMedia.length > 0 ? (
            <div 
              className="will-card-media instagram-style"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <Link to={`/will/${props.id}`} state={{ will: { ...props, media } }} onClick={handleWillClick}>
                <div className={`media-slider ${isTransitioning ? `sliding-${transitionDirection}` : ''}`}>
                  {getMediaContent(visualMedia[currentImageIndex], currentImageIndex)}
                </div>
              </Link>
              
              {visualMedia.length > 1 && (
                <>
                  <div className="image-navigation">
                    <button 
                      onClick={handlePrevImage} 
                      className="nav-button prev"
                      aria-label="Previous image"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button 
                      onClick={handleNextImage} 
                      className="nav-button next"
                      aria-label="Next image"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                  <div className="image-dots">
                    {visualMedia.map((_, index) => (
                      <span 
                        key={index} 
                        className={`dot ${index === currentImageIndex ? 'active' : ''}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <Link 
              to={`/will/${props.id}`}
              state={{ will: { ...props, media } }} 
              onClick={handleWillClick} 
              className="aspect-square bg-gray-50 flex items-center justify-center"
            >
              <Box className="h-12 w-12 text-gray-400" />
            </Link>
          )}
          
          <div className="px-4 pt-3 pb-1">
            <div className="flex items-center space-x-4">
              <button 
                onClick={handleLikeToggle}
                className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <Heart className={`h-5 w-5 ${isLiked ? 'fill-red-500 text-red-500' : ''}`} />
                <span>{likeCount > 0 ? likeCount : ''}</span>
              </button>
              
              <button
                onClick={handleCommentClick}
                className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <MessageCircle className="h-5 w-5" />
              </button>
              
              <button
                onClick={handleOfferClick}
                className="flex items-center gap-1 text-gray-600 hover:text-yellow-500 transition-colors"
              >
                <Trophy className="h-5 w-5" />
              </button>
              
              {documentMedia.length > 0 && (
                <FileAttachmentIndicator count={documentMedia.length} />
              )}
              
              {props.reward && (
                <div className="ml-auto">
                  <span className="font-bold text-lg text-black">{props.reward}</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="px-4 py-2">
            <Link to={`/will/${props.id}`} state={{ will: { ...props, media } }} onClick={handleWillClick}>
              <div className="flex items-center space-x-2 mb-2">
                <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-800">
                  {props.category}
                </span>
                <span className={`px-2 py-0.5 text-xs rounded-full ${urgencyColors[props.urgency]}`}>
                  {props.urgency}
                </span>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">{props.title}</h3>
              <p className="text-sm text-gray-600 line-clamp-2">{props.description}</p>
            </Link>
            
            {props.location && (
              <div className="text-xs text-gray-500 mt-2">
                <span className="inline-flex items-center">
                  📍 {props.location}
                </span>
              </div>
            )}
            
            {props.fulfillmentTime && (
              <div className="text-xs text-gray-500 mt-1">
                Due: {new Date(props.fulfillmentTime).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                })}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Comment Modal */}
      <CommentModal 
        isOpen={showCommentModal}
        onClose={() => setShowCommentModal(false)}
        willId={props.id}
        willTitle={props.title}
      />
      
      {/* Offer Form Modal */}
      <OfferFormModal 
        isOpen={showOfferModal}
        onClose={() => setShowOfferModal(false)}
        willId={props.id}
        willTitle={props.title}
        willMedia={media}
      />
    </div>
  );
};

export default WillCard;